import React, { useState, useEffect, useContext } from "react";

import { toast } from "react-toastify";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Stack } from "@mui/material";

export const INTEGRATION_KINDS = [
  { value: "http", label: "Requisição HTTP (webhook)" },
  { value: "sheetsAppend", label: "Google Sheets — Adicionar linha" },
  { value: "sheetsLookup", label: "Google Sheets — Buscar dado" },
  { value: "sheetsUpdate", label: "Google Sheets — Atualizar linha" },
];

const emptyConfig = () => ({
  kind: "http",
  method: "POST",
  url: "",
  headers: [],
  body: "",
  saveField: "",
  savePath: "",
  spreadsheet: "",
  sheetName: "",
  columns: ["{{data}}", "{{hora}}", "{{nome}}", "{{numero}}"],
  searchColumn: "",
  searchValue: "{{numero}}",
  returnColumn: "",
  returnMode: "single",
  returnToColumn: "",
  notFoundMessage: "",
  loop: false,
  loopMessage: "",
  exitWord: "0",
  exitMessage: "",
  notFoundValue: "",
  updates: [{ column: "", value: "" }],
});

const VARIABLES_HELP =
  "Variáveis: {{nome}}, {{numero}}, {{email}}, {{data}}, {{hora}}, o nome de um campo do contato (como {{cidade}}) ou a variável de um bloco Pergunta (como {{ncm}}).";

const FlowBuilderIntegrationModal = ({ open, onSave, data, onUpdate, close }) => {
  const { user } = useContext(AuthContext);
  const isAdmin = user?.profile === "admin";
  const [activeModal, setActiveModal] = useState(false);
  const [cfg, setCfg] = useState(emptyConfig());
  const [google, setGoogle] = useState({ configured: false, email: "" });
  const [credDialog, setCredDialog] = useState({ open: false, text: "", saving: false });
  const [testing, setTesting] = useState(false);
  const [tabs, setTabs] = useState([]);

  const set = (patch) => setCfg((old) => ({ ...old, ...patch }));
  const isSheets = cfg.kind.startsWith("sheets");

  const loadGoogle = async () => {
    try {
      const { data: status } = await api.get("/flow-integrations/google");
      setGoogle(status);
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    if (open !== "edit" && open !== "create") return;
    const base = emptyConfig();
    if (open === "edit" && data?.data?.kind) {
      setCfg({ ...base, ...data.data });
    } else {
      setCfg(base);
    }
    setTabs([]);
    loadGoogle();
    setActiveModal(true);
    // eslint-disable-next-line
  }, [open]);

  const handleClose = () => {
    close(null);
    setActiveModal(false);
  };

  const handleSaveCredentials = async () => {
    setCredDialog((old) => ({ ...old, saving: true }));
    try {
      const { data: status } = await api.put("/flow-integrations/google", {
        credentials: credDialog.text,
      });
      setGoogle(status);
      toast.success("Conta Google configurada");
      setCredDialog({ open: false, text: "", saving: false });
    } catch (error) {
      const msg = error?.response?.data?.error;
      toast.error(msg && msg !== "ERR_NO_PERMISSION" ? msg : "Não foi possível salvar. Só administradores podem configurar.");
      setCredDialog((old) => ({ ...old, saving: false }));
    }
  };

  const handleRemoveCredentials = async () => {
    try {
      const { data: status } = await api.delete("/flow-integrations/google");
      setGoogle(status);
      toast.success("Conta Google removida");
    } catch (error) {
      toast.error("Não foi possível remover. Só administradores podem alterar.");
    }
  };

  const handleTest = async () => {
    if (!cfg.spreadsheet.trim()) return toast.error("Informe o link ou ID da planilha");
    setTesting(true);
    try {
      const { data: res } = await api.post("/flow-integrations/google/test", {
        spreadsheet: cfg.spreadsheet,
      });
      setTabs(res.sheets || []);
      toast.success(`Planilha encontrada: ${res.title}`);
      if (!cfg.sheetName && res.sheets?.length) set({ sheetName: res.sheets[0] });
    } catch (error) {
      const msg = error?.response?.data?.error;
      toast.error(msg ? `Não foi possível acessar a planilha: ${msg}` : "Não foi possível acessar a planilha");
    }
    setTesting(false);
  };

  const handleSave = () => {
    if (cfg.kind === "http") {
      if (!cfg.url.trim()) return toast.error("Informe a URL");
      if (!/^https?:\/\//i.test(cfg.url.trim())) return toast.error("A URL deve começar com http:// ou https://");
    } else {
      if (!google.configured) return toast.error("Configure a conta Google primeiro");
      if (!cfg.spreadsheet.trim()) return toast.error("Informe a planilha");
      if (!cfg.sheetName.trim()) return toast.error("Informe o nome da aba");
      if (cfg.kind === "sheetsLookup") {
        if (!cfg.searchColumn.trim() || !cfg.searchValue.trim()) return toast.error("Informe a coluna e o valor da busca");
        if (!cfg.returnColumn.trim()) return toast.error("Informe a coluna do dado a buscar");
        if (cfg.returnMode !== "range" && !cfg.saveField.trim()) return toast.error("Informe o campo do contato onde guardar o dado");
      }
      if (cfg.kind === "sheetsUpdate") {
        if (!cfg.searchColumn.trim() || !cfg.searchValue.trim()) return toast.error("Informe a coluna e o valor da busca");
        if (!cfg.updates.some((u) => u.column.trim())) return toast.error("Informe ao menos uma coluna a atualizar");
      }
    }
    const payload = { ...cfg, url: cfg.url.trim(), spreadsheet: cfg.spreadsheet.trim(), sheetName: cfg.sheetName.trim() };
    if (open === "edit") {
      onUpdate({ ...data, data: payload });
    } else {
      onSave(payload);
    }
    handleClose();
  };

  const rowBox = {
    display: "flex",
    gap: 8,
    alignItems: "center",
    marginBottom: 8,
  };

  const field = (label, key, extra = {}) => (
    <TextField
      variant="outlined"
      size="small"
      fullWidth
      label={label}
      value={cfg[key]}
      onChange={(e) => set({ [key]: e.target.value })}
      style={{ marginBottom: 12 }}
      {...extra}
    />
  );

  const listEditor = (listKey, items, render, emptyItem) => (
    <>
      {items.map((item, index) => (
        <div key={index} style={rowBox}>
          {render(item, (patch) =>
            set({ [listKey]: items.map((it, i) => (i === index ? { ...it, ...patch } : it)) })
          )}
          <IconButton
            size="small"
            onClick={() => set({ [listKey]: items.filter((_, i) => i !== index) })}
          >
            <DeleteOutlineIcon />
          </IconButton>
        </div>
      ))}
      <Button
        variant="outlined"
        color="primary"
        size="small"
        onClick={() => set({ [listKey]: [...items, emptyItem] })}
      >
        + Adicionar
      </Button>
    </>
  );

  return (
    <div>
      <Dialog open={activeModal} onClose={handleClose} fullWidth maxWidth="sm" scroll="paper">
        <DialogTitle>{open === "create" ? "Adicionar integração ao fluxo" : "Editar integração"}</DialogTitle>
        <Stack>
          <DialogContent dividers>
            <Select
              value={cfg.kind}
              fullWidth
              onChange={(e) => set({ kind: e.target.value })}
              style={{ marginBottom: 16 }}
            >
              {INTEGRATION_KINDS.map((k) => (
                <MenuItem key={k.value} value={k.value}>
                  {k.label}
                </MenuItem>
              ))}
            </Select>

            {cfg.kind === "http" && (
              <>
                <div style={{ display: "flex", gap: 8 }}>
                  <Select
                    value={cfg.method}
                    onChange={(e) => set({ method: e.target.value })}
                    style={{ width: 110, marginBottom: 12 }}
                  >
                    {["GET", "POST", "PUT", "PATCH", "DELETE"].map((m) => (
                      <MenuItem key={m} value={m}>
                        {m}
                      </MenuItem>
                    ))}
                  </Select>
                  {field("URL", "url", { placeholder: "https://exemplo.com/webhook" })}
                </div>
                <Typography variant="subtitle2" style={{ marginBottom: 6 }}>
                  Cabeçalhos (opcional)
                </Typography>
                {listEditor(
                  "headers",
                  cfg.headers,
                  (item, patch) => (
                    <>
                      <TextField
                        variant="outlined"
                        size="small"
                        label="Nome"
                        value={item.key}
                        onChange={(e) => patch({ key: e.target.value })}
                      />
                      <TextField
                        variant="outlined"
                        size="small"
                        fullWidth
                        label="Valor"
                        value={item.value}
                        onChange={(e) => patch({ value: e.target.value })}
                      />
                    </>
                  ),
                  { key: "", value: "" }
                )}
                {cfg.method !== "GET" &&
                  field("Corpo da requisição", "body", {
                    multiline: true,
                    minRows: 4,
                    placeholder: '{ "nome": "{{nome}}", "telefone": "{{numero}}" }',
                    style: { marginTop: 12, marginBottom: 12 },
                  })}
                <Typography variant="subtitle2" style={{ marginBottom: 6 }}>
                  Guardar a resposta (opcional)
                </Typography>
                <div style={{ display: "flex", gap: 8 }}>
                  {field("Campo do contato", "saveField")}
                  {field("Caminho na resposta JSON", "savePath", { placeholder: "ex.: data.id" })}
                </div>
              </>
            )}

            {isSheets && (
              <>
                <div
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 8,
                    padding: 12,
                    marginBottom: 16,
                    background: google.configured ? "#f0fdf4" : "#fffbeb",
                  }}
                >
                  {google.configured ? (
                    <>
                      <Typography variant="body2">
                        Conta Google configurada. <b>Compartilhe a planilha, como Editor, com este e-mail:</b>
                      </Typography>
                      <Typography
                        variant="body2"
                        style={{ fontFamily: "monospace", wordBreak: "break-all", margin: "6px 0" }}
                      >
                        {google.email}
                      </Typography>
                      <Button
                        size="small"
                        onClick={() => {
                          try {
                            navigator.clipboard.writeText(google.email);
                            toast.success("E-mail copiado");
                          } catch (e) {}
                        }}
                      >
                        Copiar e-mail
                      </Button>
                      {isAdmin && (
                        <>
                          <Button size="small" onClick={() => setCredDialog({ open: true, text: "", saving: false })}>
                            Trocar credencial
                          </Button>
                          <Button size="small" color="secondary" onClick={handleRemoveCredentials}>
                            Remover
                          </Button>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <Typography variant="body2" style={{ marginBottom: 8 }}>
                        Para usar o Google Sheets é preciso conectar uma conta de serviço do Google.
                      </Typography>
                      {isAdmin ? (
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          onClick={() => setCredDialog({ open: true, text: "", saving: false })}
                        >
                          Configurar conta Google
                        </Button>
                      ) : (
                        <Typography variant="caption">Peça a um administrador para configurar.</Typography>
                      )}
                    </>
                  )}
                </div>

                <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                  {field("Link ou ID da planilha", "spreadsheet", { placeholder: "https://docs.google.com/spreadsheets/d/..." })}
                  <Button
                    variant="outlined"
                    color="primary"
                    disabled={testing || !google.configured}
                    onClick={handleTest}
                    style={{ whiteSpace: "nowrap", height: 40 }}
                  >
                    Testar
                  </Button>
                </div>
                {tabs.length > 0 ? (
                  <Select
                    value={tabs.includes(cfg.sheetName) ? cfg.sheetName : ""}
                    displayEmpty
                    fullWidth
                    onChange={(e) => set({ sheetName: e.target.value })}
                    style={{ marginBottom: 12 }}
                    renderValue={(v) => v || "Selecione a aba"}
                  >
                    {tabs.map((t) => (
                      <MenuItem key={t} value={t}>
                        {t}
                      </MenuItem>
                    ))}
                  </Select>
                ) : (
                  field("Nome da aba", "sheetName", { placeholder: "Ex.: Página1" })
                )}

                {cfg.kind === "sheetsAppend" && (
                  <>
                    <Typography variant="subtitle2" style={{ marginBottom: 6 }}>
                      Valores da nova linha (um por coluna, da A em diante)
                    </Typography>
                    {cfg.columns.map((c, index) => (
                      <div key={index} style={rowBox}>
                        <TextField
                          variant="outlined"
                          size="small"
                          fullWidth
                          label={`Coluna ${String.fromCharCode(65 + (index % 26))}`}
                          value={c}
                          onChange={(e) =>
                            set({ columns: cfg.columns.map((v, i) => (i === index ? e.target.value : v)) })
                          }
                        />
                        <IconButton
                          size="small"
                          onClick={() => set({ columns: cfg.columns.filter((_, i) => i !== index) })}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      </div>
                    ))}
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      onClick={() => set({ columns: [...cfg.columns, ""] })}
                    >
                      + Adicionar coluna
                    </Button>
                  </>
                )}

                {(cfg.kind === "sheetsLookup" || cfg.kind === "sheetsUpdate") && (
                  <>
                    <div style={{ display: "flex", gap: 8 }}>
                      {field("Coluna da busca (letra ou título)", "searchColumn", { placeholder: "Ex.: B ou Telefone" })}
                      {field("Valor procurado", "searchValue")}
                    </div>
                  </>
                )}

                {cfg.kind === "sheetsLookup" && (
                  <>
                    <Select
                      value={cfg.returnMode}
                      fullWidth
                      onChange={(e) => set({ returnMode: e.target.value })}
                      style={{ marginBottom: 12 }}
                    >
                      <MenuItem value="single">Buscar uma coluna e guardar em um campo do contato</MenuItem>
                      <MenuItem value="range">Buscar várias colunas e enviar ao cliente (com os títulos)</MenuItem>
                    </Select>
                    {cfg.returnMode === "range" ? (
                      <>
                        <div style={{ display: "flex", gap: 8 }}>
                          {field("Da coluna", "returnColumn", { placeholder: "Ex.: B" })}
                          {field("Até a coluna", "returnToColumn", { placeholder: "Ex.: N" })}
                        </div>
                        {field("Mensagem se não encontrar", "notFoundMessage", {
                          placeholder: "Não encontrei {{busca}} na planilha.",
                        })}
                        <Select
                          value={cfg.loop ? "loop" : "end"}
                          fullWidth
                          onChange={(e) => set({ loop: e.target.value === "loop" })}
                          style={{ marginBottom: 12 }}
                        >
                          <MenuItem value="end">Depois de responder, encerrar o fluxo</MenuItem>
                          <MenuItem value="loop">Depois de responder, perguntar de novo (precisa vir logo após um bloco Pergunta)</MenuItem>
                        </Select>
                        {cfg.loop && (
                          <>
                            {field("Mensagem para perguntar de novo", "loopMessage", {
                              placeholder: "Digite outro valor para consultar ou 0 para sair.",
                            })}
                            <div style={{ display: "flex", gap: 8 }}>
                              {field("Palavra para sair", "exitWord", { placeholder: "0" })}
                              {field("Mensagem ao sair", "exitMessage", { placeholder: "Consulta encerrada." })}
                            </div>
                          </>
                        )}
                        <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 8 }}>
                          O cliente recebe uma linha por coluna preenchida, no formato "Título: valor". Use {"{{busca}}"} para o valor procurado.
                        </Typography>
                      </>
                    ) : (
                      <>
                        {field("Coluna do dado a buscar (letra ou título)", "returnColumn", { placeholder: "Ex.: D ou Saldo" })}
                        {field("Guardar no campo do contato", "saveField", { placeholder: "Ex.: saldo" })}
                        {field("Se não encontrar, guardar", "notFoundValue", { placeholder: "(vazio)" })}
                      </>
                    )}
                  </>
                )}

                {cfg.kind === "sheetsUpdate" && (
                  <>
                    <Typography variant="subtitle2" style={{ marginBottom: 6 }}>
                      Colunas a atualizar na linha encontrada
                    </Typography>
                    {listEditor(
                      "updates",
                      cfg.updates,
                      (item, patch) => (
                        <>
                          <TextField
                            variant="outlined"
                            size="small"
                            label="Coluna"
                            value={item.column}
                            onChange={(e) => patch({ column: e.target.value })}
                          />
                          <TextField
                            variant="outlined"
                            size="small"
                            fullWidth
                            label="Novo valor"
                            value={item.value}
                            onChange={(e) => patch({ value: e.target.value })}
                          />
                        </>
                      ),
                      { column: "", value: "" }
                    )}
                  </>
                )}
              </>
            )}

            <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 12 }}>
              {VARIABLES_HELP}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color="secondary" variant="outlined">
              {i18n.t("contactModal.buttons.cancel")}
            </Button>
            <Button color="primary" variant="contained" onClick={handleSave}>
              {open === "create" ? "Adicionar" : "Editar"}
            </Button>
          </DialogActions>
        </Stack>
      </Dialog>

      <Dialog
        open={credDialog.open}
        onClose={() => setCredDialog((old) => ({ ...old, open: false }))}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Conta de serviço do Google</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" component="div" style={{ marginBottom: 12 }}>
            <ol style={{ paddingLeft: 18, margin: 0 }}>
              <li>Acesse console.cloud.google.com e crie um projeto (ou use um existente).</li>
              <li>Em "APIs e serviços", ative a <b>Google Sheets API</b>.</li>
              <li>Em "Credenciais", crie uma <b>Conta de serviço</b>.</li>
              <li>Abra a conta criada, vá em "Chaves", adicione uma chave do tipo <b>JSON</b> e baixe o arquivo.</li>
              <li>Cole abaixo o conteúdo inteiro do arquivo.</li>
              <li>Na planilha, clique em Compartilhar e dê acesso de <b>Editor</b> ao e-mail da conta de serviço.</li>
            </ol>
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={6}
            variant="outlined"
            label="Conteúdo do arquivo JSON"
            value={credDialog.text}
            onChange={(e) => setCredDialog((old) => ({ ...old, text: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => setCredDialog((old) => ({ ...old, open: false }))}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={credDialog.saving || !credDialog.text.trim()}
            onClick={handleSaveCredentials}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default FlowBuilderIntegrationModal;
